import React, { useEffect, useState, useRef, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { UIContext } from "../../providers/UIProvider";
import { useData } from "../../providers/DataProvider";
import httpClient, { TimeoutError } from "../../utils/httpClient";
import { downloadFile } from "../../utils/downloadFile";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress
} from "@mui/material";

interface PayrollReport {
  bonusApplicationId: number;
  year: number;
  quarter: number;
  employeeNumber: string;
  employeeName: string;
  email: string;
  applicationType: string;
  projectNumbers: string;
  subdivisions: string;
  amount: number;
}

const formatDateTime = (): string => {
  const now = new Date();
  const centralTime = now.toLocaleString('sv-SE', {
    timeZone: 'America/Chicago',
    hour12: false
  });
  // "2025-11-12 15:30:45" -> "20251112_153045"
  return centralTime.replace(/[-: ]/g, '').replace(/(\d{8})(\d{6})/, '$1_$2');
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

type UploadModalProps = {
  open: boolean;
  file: File | null;
  uploading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const UploadModal = ({ open, file, uploading, onConfirm, onCancel }: UploadModalProps) => (
  <Dialog open={open} onClose={uploading ? undefined : onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>Confirm File Upload</DialogTitle>
    <DialogContent>
      {file && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            File Details:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Name: {file.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Size: {(file.size / 1024).toFixed(2)} KB
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Type: {file.type || 'application/xlsx'}
          </Typography>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Uploading file...
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={uploading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color="primary"
        disabled={uploading}
      >
        {uploading ? 'Uploading...' : 'Upload'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default function Payroll() {
  const { role } = useData();
  const { accounts } = useMsal();
  const [state, dispatch] = useContext<any>(UIContext);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [rows, setRows] = useState<PayrollReport[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isPayroll = accounts[0].idTokenClaims?.roles?.includes("Payroll");

  const [bonusType, setBonusType] = useState<string>(
    localStorage.getItem("bonusType") || "ClosingBonus"
  );

  const downloadReport = async () => {
    if (
      window.confirm(
        "Are you sure you want to submit the current payroll report? You will receive an automatic download."
      )
    ) {
      setLoadingReport(true);
      const formattedDate = formatDateTime();
      const filename = `QBA_PayoutInProgress_${formattedDate}.xlsx`;
      try {
        // Prepare the payload
        const payload = {
          applications: rows.map(row => ({
            bonusApplicationId: row.bonusApplicationId,
            userAction: "Payroll_Processing",
            amount: row.amount,
            bonusSheet: null
          })),
          comments: [
            `Bulk action performed by ${accounts[0]?.username || 'Payroll User'}`,
            "Approved by Payroll"
          ]
        };

        const response = await httpClient.post(
          "/api/update-payment-process-and-get-spreadsheet",
          payload,
          {
            responseType: 'blob',
            headers: {
              roleType: role
            }
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Refresh data so the latest payroll state is shown after download
        await loadTable();

        // Notify user that the payroll report was successfully downloaded
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Payroll report downloaded successfully.",
            severity: "success",
          },
        });
      } catch (error: unknown) {
        if (error instanceof TimeoutError) {
          console.error(`Request timed out for ${(error as TimeoutError).config.method.toUpperCase()} ${(error as TimeoutError).config.url} — ${(error as TimeoutError).message}. Please check if the payroll report was downloaded successfully before trying again`);
          dispatch({
            type: "Snackbar",
            payload: {
              show: true,
              message: "Request timed out. Please check if the download went through before trying again.",
              severity: "error",
            },
          });
        } else {
          console.error("Failed to download report:", error);
          dispatch({
            type: "Snackbar",
            payload: {
              show: true,
              message: "Failed to download report. Please try again.",
              severity: "error",
            },
          });
        }

      } finally {
        setLoadingReport(false);
      }
    }
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return 'Please upload only Excel files (.xlsx or .xls)';
    }

    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const error = validateFile(file);

      if (error) {
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: error,
            severity: "error",
          },
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setSelectedFile(file);
      setUploadModalOpen(true);
    }
  };

  const handleUploadConfirm = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("File", selectedFile);
    setUploading(true);

    try {
      await httpClient.post(
        "/api/upload-payment-details-spreadsheet",
        formData,
        {
          headers: {
            roleType: role
          }
        }
      );
      dispatch({
        type: "Snackbar",
        payload: {
          show: true,
          message: "Payment details spreadsheet uploaded successfully",
          severity: "success",
        },
      });
      await loadTable();
    } catch (error) {
      if (error instanceof TimeoutError) {
        const timeoutError = error as TimeoutError & { config?: { method?: string; url?: string }; message?: string };
        console.error(`Request timed out for ${timeoutError.config?.method?.toUpperCase()} ${timeoutError.config?.url} — ${timeoutError.message}. Please check if the upload went through before trying again`);
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Request timed out. Please check if the upload went through before trying again.",
            severity: "error",
          },
        });
      } else {
        console.error("Failed to upload payment details spreadsheet:", error);
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Failed to upload payment details spreadsheet. Please try again.",
            severity: "error",
          },
        });
      }
    } finally {
      setUploading(false);
      setUploadModalOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadCancel = () => {
    setUploadModalOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const loadTable = async () => {
    setLoading(true);
    try {
      const res = await httpClient.get<PayrollReport[]>(
        "/api/payout-details",
        {
          headers: {
            roleType: role,
          },
        }
      );

      if (res && res.data) {
        setRows(res.data);
      }
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Request timed out. Please check if the data loaded before trying again.",
            severity: "error",
          },
        });
      } else {
        console.error("Failed to load table data:", error);
        dispatch({
          type: "Snackbar",
          payload: {
            show: true,
            message: "Failed to load payroll data. Please try again.",
            severity: "error",
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const selectedRole = localStorage.getItem("selectedRole") || "";
    if (selectedRole !== "Payroll") {
      navigate("/action-items");
      return;
    }
    loadTable();
  }, [navigate]);

  useEffect(() => {
    if (accounts[0].idTokenClaims?.roles && !isPayroll) {
      navigate("/");
    }
  }, [isPayroll, accounts, navigate]);

  const showInstructions = () => {
    alert(
      "How to use this page:\n\n" +
      "1. The table shows the current payroll report.\n" +
      "2. Use the 'Upload Report' button to upload a new payroll spreadsheet.\n" +
      "3. Click 'Download Report' to generate and download the current payroll report.\n" +
      "4. Ensure all projects are approved before generating the final report."
    );
  };

  return (
    <main className="flex-grow p-4">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center align-middle">
              <h2 className="text-xl font-semibold">Payroll Report</h2>
              <button
                onClick={showInstructions}
                className="ml-2 text-gray-500 hover:text-gray-700"
                title="How to use this page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Upload Report
              </button>
              <button
                onClick={downloadReport}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {loadingReport ? (
                  <div className="flex items-center">
                    <CircularProgress size={20} className="mr-2" color="inherit" />
                    Generating...
                  </div>
                ) : (
                  'Download Report'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Year & Quarter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Subdivision
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <CircularProgress />
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.bonusApplicationId} className="hover:bg-gray-100">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{row.employeeName}</div>
                      <div className="text-xs text-gray-400">Bonus Application #: {row.bonusApplicationId}</div>
                      <div className="text-xs text-gray-400">Employee #: {row.employeeNumber}</div>
                      <div className="text-xs text-gray-400">{row.email}</div>

                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.year}-Q{row.quarter}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.applicationType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.projectNumbers}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.subdivisions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="py-20">
                      <h6 className="text-xl font-semibold">
                        No items in payroll report.
                      </h6>
                      <p className="text-gray-500 mt-2">
                        No payroll data available.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      `
      <UploadModal
        open={uploadModalOpen}
        file={selectedFile}
        uploading={uploading}
        onConfirm={handleUploadConfirm}
        onCancel={handleUploadCancel}
      />
    </main>
  );
}
